import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, MousePointerClick, CalendarDays, AlarmClock, ChevronDown } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { DownloadButton } from '@/components/marketing/DownloadButton';
import { FeatureCard } from '@/components/marketing/FeatureCard';
import { AlertOverlayMock, ScreenshotFrame } from '@/components/marketing/ScreenshotFrame';
import { RouteHead } from '@/components/seo/RouteHead';
import { StructuredData } from '@/components/seo/StructuredData';
import { ROUTES } from '@/routes';
import { env } from '@/lib/env';
import { PLATFORMS } from '@/lib/db';
import { fadeUp, fadeIn, scaleIn, staggerContainer, VIEWPORT_REVEAL, STAGGER } from '@/lib/motion';

export default function HomePage() {
  const { t } = useTranslation(['home', 'seo']);

  const featureCards = [
    { id: 'alert', icon: Bell, title: t('features.fullscreen_alert.title'), desc: t('features.fullscreen_alert.description') },
    { id: 'join', icon: MousePointerClick, title: t('features.one_click_join.title'), desc: t('features.one_click_join.description') },
    { id: 'sync', icon: CalendarDays, title: t('features.calendar_sync.title'), desc: t('features.calendar_sync.description') },
    { id: 'snooze', icon: AlarmClock, title: t('features.snooze.title'), desc: t('features.snooze.description') },
  ];

  const integrationNames = [
    t('integrations.services.google_meet'),
    t('integrations.services.zoom'),
    t('integrations.services.teams'),
    t('integrations.services.webex'),
    t('integrations.services.discord'),
    t('integrations.services.slack'),
  ];

  const faqItems = [
    { id: 'free', q: t('faq_teaser.free_question'), a: t('faq_teaser.free_answer') },
    { id: 'platforms', q: t('faq_teaser.platforms_question'), a: t('faq_teaser.platforms_answer') },
    { id: 'calendar', q: t('faq_teaser.calendar_question'), a: t('faq_teaser.calendar_answer') },
  ];

  return (
    <>
      <RouteHead
        path={ROUTES.home}
        titleKey="seo:home.title"
        descriptionKey="seo:home.description"
        ogDescriptionKey="seo:home.og_description"
      />
      <StructuredData
        schema={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: t('seo:site.name'),
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Windows, macOS',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          description: t('seo:home.description'),
          url: env.VITE_SITE_URL.replace(/\/+$/, ''),
        }}
      />
      {/* Hero */}
      <Section padding="lg" className="relative overflow-hidden">
        <Container size="md" className="relative z-10 text-center">
          <motion.div variants={staggerContainer(STAGGER.base)} initial="hidden" animate="visible">
            <motion.div variants={fadeUp}>
              <Heading level={1}>{t('hero.headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
              {t('hero.subheadline')}
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
            >
              <DownloadButton />
              <Link
                to={ROUTES.howItWorks}
                className="inline-flex h-12 items-center gap-2 rounded-md border border-border px-6 text-sm font-semibold text-foreground transition-colors duration-fast ease-out-quart hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {t('hero.cta_secondary')}
              </Link>
            </motion.div>
          </motion.div>
          <motion.div
            variants={scaleIn}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
            className="mx-auto mt-16 max-w-3xl"
          >
            <ScreenshotFrame
              chrome={PLATFORMS.macos}
              title={t('hero.demo_label')}
              label={t('hero.demo_label')}
            >
              <AlertOverlayMock />
            </ScreenshotFrame>
          </motion.div>
        </Container>
      </Section>

      {/* Problem */}
      <Section padding="md" bordered>
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('problem.headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-xl text-muted-foreground">
              {t('problem.body')}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* Features */}
      <Section padding="lg">
        <Container size="xl">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
            className="mb-12 text-center"
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('features.headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-xl text-muted-foreground">
              {t('features.subheadline')}
            </motion.p>
          </motion.div>
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {featureCards.map(({ id, icon, title, desc }) => (
              <motion.div key={id} variants={fadeUp}>
                <FeatureCard icon={icon} title={title} description={desc} className="h-full" />
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Integrations */}
      <Section padding="md" bordered>
        <Container size="lg">
          <motion.div
            variants={staggerContainer(STAGGER.tight)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
            className="text-center"
          >
            <motion.p
              variants={fadeIn}
              className="text-xs font-medium uppercase tracking-widest text-muted-foreground"
            >
              {t('integrations.label')}
            </motion.p>
            <motion.ul
              variants={staggerContainer(STAGGER.tight)}
              className="mt-6 flex flex-wrap items-center justify-center gap-3"
            >
              {integrationNames.map((name) => (
                <motion.li key={name} variants={fadeIn}>
                  <span className="inline-block rounded-full border border-border bg-card px-4 py-1.5 text-sm text-muted-foreground">
                    {name}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </Container>
      </Section>

      {/* Screenshot showcase */}
      <Section padding="lg">
        <Container size="lg">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div
              variants={staggerContainer(STAGGER.base)}
              initial="hidden"
              whileInView="visible"
              viewport={VIEWPORT_REVEAL}
            >
              <motion.div variants={fadeUp}>
                <Heading level={2}>{t('screenshot.headline')}</Heading>
              </motion.div>
              <motion.p variants={fadeUp} className="mt-4 text-muted-foreground">
                {t('screenshot.subheadline')}
              </motion.p>
            </motion.div>
            <motion.div
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={VIEWPORT_REVEAL}
            >
              <ScreenshotFrame
                chrome={PLATFORMS.macos}
                title={t('screenshot.caption')}
                label={t('screenshot.caption')}
              >
                <AlertOverlayMock />
              </ScreenshotFrame>
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* FAQ teaser */}
      <Section padding="md" bordered>
        <Container size="md">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp} className="mb-8 text-center">
              <Heading level={2}>{t('faq_teaser.headline')}</Heading>
            </motion.div>
            <motion.ul
              variants={staggerContainer(STAGGER.base)}
              className="flex flex-col gap-3"
            >
              {faqItems.map(({ id, q, a }) => (
                <motion.li key={id} variants={fadeUp}>
                  <details className="group rounded-lg border border-border bg-card p-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
                      {q}
                      <ChevronDown
                        className="ms-4 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out-quart group-open:rotate-180"
                        aria-hidden="true"
                      />
                    </summary>
                    <p className="mt-3 text-sm text-muted-foreground">{a}</p>
                  </details>
                </motion.li>
              ))}
            </motion.ul>
            <motion.div variants={fadeUp} className="mt-8 text-center">
              <Link
                to={ROUTES.faq}
                className="text-sm font-medium text-brand underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {t('faq_teaser.see_all')}
              </Link>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* Final CTA */}
      <Section padding="lg" className="bg-primary-soft">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('final_cta.headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-md text-muted-foreground">
              {t('final_cta.subheadline')}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex justify-center">
              <DownloadButton />
            </motion.div>
          </motion.div>
        </Container>
      </Section>
    </>
  );
}
